import type { RenderObject, Vec3 } from '@v2/contracts'
import { multiplyQuaternion, quaternionFromAxisAngle, slerpQuaternion } from '@v2/math'

type Quaternion = readonly [number, number, number, number]
export interface PiecePose {
  readonly position: Vec3
  readonly rotation: number
  readonly orientation?: Quaternion
  readonly size: Vec3
}
const orientationOf = (pose: PiecePose): Quaternion => pose.orientation ?? quaternionFromAxisAngle([0, 1, 0], pose.rotation)
const mix3 = (from: Vec3, to: Vec3, weight: number): Vec3 => [
  from[0] + (to[0] - from[0]) * weight,
  from[1] + (to[1] - from[1]) * weight,
  from[2] + (to[2] - from[2]) * weight,
]
const ease = (t: number): number => 1 - Math.pow(1 - t, 3)

export function poseOf(object: PiecePose): PiecePose {
  return { position: object.position, rotation: object.rotation, orientation: object.orientation, size: object.size }
}

export function differentPose(a: PiecePose, b: PiecePose): boolean {
  const qa = orientationOf(a), qb = orientationOf(b)
  const dot = Math.abs(qa.reduce((sum, value, index) => sum + value * qb[index], 0))
  return a.position.some((value, index) => Math.abs(value - b.position[index]) > 0.001)
    || a.size.some((value, index) => Math.abs(value - b.size[index]) > 0.001)
    || dot < 0.999999
}

export function interpolatePose(from: PiecePose, to: PiecePose, progress: number, lift: number, departure = false): PiecePose {
  if (progress <= 0) return from
  if (progress >= 1) return to
  const weight = departure ? progress * progress * (3 - 2 * progress) : ease(progress)
  const position = mix3(from.position, to.position, weight)
  // A single soft arc arrives with zero vertical velocity. No bouncing or idle
  // motion competes with reading a card after it has landed.
  const envelope = Math.pow(Math.sin(progress * Math.PI), 2)
  const arc = envelope * lift
  let offset: Vec3 = [0, arc, 0]
  if (departure && from.orientation) {
    // A held card's local -Z points toward the top of the screen. Lift it out of
    // its neighbors before it shrinks into the distant tabletop perspective.
    const [x, y, z, w] = from.orientation
    const up: Vec3 = [-2 * (x * z + w * y), -2 * (y * z - w * x), -(1 - 2 * (x * x + y * y))]
    const height = envelope * Math.max(1.2, from.size[2] * 3.2)
    offset = [up[0] * height, up[1] * height, up[2] * height]
  }
  const yaw = Math.atan2(Math.sin(to.rotation - from.rotation), Math.cos(to.rotation - from.rotation))
  return {
    position: [position[0] + offset[0], position[1] + offset[1], position[2] + offset[2]],
    rotation: from.rotation + yaw * weight,
    orientation: from.orientation || to.orientation ? slerpQuaternion(orientationOf(from), orientationOf(to), weight) : undefined,
    size: mix3(from.size, to.size, weight),
  }
}

/** Two small packets split, interleave and square up. Only current backs exist. */
export function shufflePackets(deck: RenderObject, progress: number): readonly RenderObject[] {
  const time = Math.max(0, Math.min(1, progress))
  const spread = time < 0.32 ? ease(time / 0.32)
    : time < 0.66 ? 1 - ease((time - 0.32) / 0.34) : 0
  const envelope = Math.pow(Math.sin(time * Math.PI), 2)
  const back = deck.back ?? { title: 'Unrevealed', color: '#345b51', background: '#e8dfc8', style: 'ornament' as const }
  return Array.from({ length: 6 }, (_, index): RenderObject => {
    const packet = index % 2 === 0 ? -1 : 1
    const layer = Math.floor(index / 2)
    const localX = packet * deck.size[0] * 0.48 * spread
    const localZ = packet * deck.size[2] * 0.035 * spread
    const cosine = Math.cos(deck.rotation), sine = Math.sin(deck.rotation)
    const riffle = Math.sin(Math.max(0, Math.min(1, (time - 0.31 - layer * 0.035) / 0.39)) * Math.PI) * 0.09
    return {
      ...deck,
      id: index === 5 ? deck.id : `__table:shuffle:${deck.id}:${index}`,
      position: [
        deck.position[0] + localX * cosine + localZ * sine,
        deck.position[1] + (index - 5) * 0.02 + envelope * (0.13 + (packet > 0 ? 0.06 : 0)) + riffle,
        deck.position[2] - localX * sine + localZ * cosine,
      ],
      rotation: deck.rotation + packet * spread * 0.12,
      orientation: deck.orientation ? multiplyQuaternion(deck.orientation, quaternionFromAxisAngle([0, 1, 0], packet * spread * 0.12)) : undefined,
      face: back,
      back,
      decorative: index === 5 ? deck.decorative : true,
      interactive: index === 5 ? deck.interactive : false,
      inspectable: false,
      highlighted: false,
    }
  })
}
